import { useQuery as useApolloQuery } from '@apollo/client/react';
import { GET_FLOW_ACTIVITY } from '../../../lib/graphql/queries/organizations';
import { mapGQLCall, type GQLCall, type GQLCallSummary } from './useFlowCallsGQL';
import type { FlowActivityChannel } from '../../../components/features/flows/flowActivityChannels';

export const FLOW_ACTIVITY_PAGE_SIZE = 25;

export interface FlowThread {
  threadId: string;
  conversationId: string | null;
  contactId: string | null;
  contactName: string | null;
  channel: string;
  messageCount: number;
  lastMessageAt: string | null;
  lastMessageSubject: string | null;
  lastMessagePreview: string | null;
}

export interface GQLFlowActivityItem {
  kind: string;
  itemId: string;
  at: string | null;
  call: GQLCallSummary | null;
  thread: FlowThread | null;
  contactId?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
}

/** The row's contact as stored now; phone and email may be missing. */
export interface FlowActivityContact {
  id: string | null;
  phone: string | null;
  email: string | null;
}

interface GetFlowActivityResult {
  flowActivity: { items: GQLFlowActivityItem[]; total: number; hasMore: boolean };
}

/** One row of the flow Activity table: a call, or an SMS / email thread. */
export type FlowActivityItem = { key: string; at: string | null; contact: FlowActivityContact } & (
  { channel: 'call'; data: GQLCall } | { channel: 'sms' | 'email'; data: FlowThread }
);

/** A row the table can render, or null for a kind this client doesn't know. */
export function mapFlowActivityItem(item: GQLFlowActivityItem): FlowActivityItem | null {
  const key = `${item.kind}-${item.itemId}`;
  const contact: FlowActivityContact = {
    id: item.contactId ?? item.call?.contactId ?? item.thread?.contactId ?? null,
    phone: item.contactPhone ?? null,
    email: item.contactEmail ?? null,
  };
  if (item.kind === 'call' && item.call) {
    return { key, at: item.at, contact, channel: 'call', data: mapGQLCall(item.call) };
  }
  if ((item.kind === 'sms' || item.kind === 'email') && item.thread) {
    return { key, at: item.at, contact, channel: item.kind, data: item.thread };
  }
  return null;
}

/**
 * One page of a flow's calls and SMS / email threads, newest first. The server
 * orders and pages all channels together, so `total` is the row count across
 * the picked channels.
 */
export function useFlowActivityGQL({
  organizationId,
  flowId,
  channels,
  page,
  search,
  enabled,
}: {
  organizationId: string;
  flowId: string;
  channels: FlowActivityChannel[];
  page: number;
  /** Matches the row's contact by name, email, phone or id. */
  search?: string;
  enabled: boolean;
}) {
  const { data, loading, error } = useApolloQuery<GetFlowActivityResult>(GET_FLOW_ACTIVITY, {
    variables: {
      organizationId,
      flowId,
      channels,
      limit: FLOW_ACTIVITY_PAGE_SIZE,
      offset: page * FLOW_ACTIVITY_PAGE_SIZE,
      search: search?.trim() || undefined,
    },
    skip: !enabled || !organizationId || !flowId,
    fetchPolicy: 'cache-and-network',
  });

  const items = (data?.flowActivity.items ?? [])
    .map(mapFlowActivityItem)
    .filter((i): i is FlowActivityItem => i !== null);

  return {
    items,
    total: data?.flowActivity.total ?? 0,
    hasMore: data?.flowActivity.hasMore ?? false,
    isLoading: loading,
    error: error ?? null,
  };
}
